// Contrato del inicio de sesión del administrador de la plataforma (EP-12, HU-044, CU-022).
// Panel y sesión completamente aparte de `session.ts` (tenants): ninguna de estas formas
// autentica una petición de ningún panel de tienda, y viceversa. Lo usan la api (guarda y
// controlador de plataforma) y apps/platform-admin (formularios).
import { z } from 'zod';
import { PASSWORD_MAX_LENGTH } from './signup.js';

export const platformLoginSchema = z.object({
  email: z
    .string({ error: 'Escribe tu correo' })
    .trim()
    .toLowerCase()
    .max(254, { error: 'Correo demasiado largo' })
    .pipe(z.email({ error: 'Escribe un correo válido' })),
  password: z
    .string({ error: 'Escribe tu contraseña' })
    .min(1, { error: 'Escribe tu contraseña' })
    .max(PASSWORD_MAX_LENGTH, { error: `Máximo ${PASSWORD_MAX_LENGTH} caracteres` }),
});

export type PlatformLoginInput = z.input<typeof platformLoginSchema>;
export type PlatformLoginData = z.output<typeof platformLoginSchema>;
export type PlatformLoginField = keyof PlatformLoginData;

/** Código TOTP de 6 dígitos (login y alta del 2FA, CA-01/CA-04). */
export const platformTotpCodeSchema = z
  .string({ error: 'Escribe el código de 6 dígitos' })
  .trim()
  .regex(/^\d{6}$/, { error: 'El código debe tener 6 dígitos' });

/** El código llega solo cuando la cuenta ya tiene el 2FA activo (CA-01, segundo paso). */
export const platformLoginRequestSchema = platformLoginSchema.extend({
  totpCode: platformTotpCodeSchema.optional(),
});
export type PlatformLoginRequestData = z.output<typeof platformLoginRequestSchema>;

export const platformVerifyTotpSetupSchema = z.object({
  totpCode: platformTotpCodeSchema,
});

export const platformCreateAdminSchema = z.object({
  name: z.string({ error: 'Escribe un nombre' }).trim().min(1).max(200),
  email: z
    .string({ error: 'Escribe un correo' })
    .trim()
    .toLowerCase()
    .max(254, { error: 'Correo demasiado largo' })
    .pipe(z.email({ error: 'Escribe un correo válido' })),
  password: z
    .string({ error: 'Escribe una contraseña' })
    .min(8, { error: 'Mínimo 8 caracteres' })
    .max(PASSWORD_MAX_LENGTH, { error: `Máximo ${PASSWORD_MAX_LENGTH} caracteres` }),
});
export type PlatformCreateAdminData = z.output<typeof platformCreateAdminSchema>;

export const PLATFORM_INVALID_CREDENTIALS_MESSAGE = 'Credenciales incorrectas';
export const PLATFORM_LOGIN_RATE_LIMITED_MESSAGE = 'Demasiados intentos. Inténtalo más tarde.';
export const PLATFORM_INVALID_TOTP_MESSAGE = 'Código incorrecto';

export type PlatformAuthErrorCode =
  | 'validation_failed'
  | 'invalid_credentials'
  | 'rate_limited'
  | 'totp_required'
  | 'totp_setup_required'
  | 'invalid_totp'
  | 'unauthenticated'
  | 'email_taken';

export interface PlatformAuthErrorBody {
  code: PlatformAuthErrorCode;
  message: string;
  fieldErrors?: Record<string, string>;
}

/** Lo que ve el panel de plataforma de su propia cuenta (nunca el secreto TOTP). */
export interface PlatformSessionInfo {
  admin: { id: string; name: string; email: string; totpEnabled: boolean };
}
